import { m } from '@sohanscript/i18n';
import { getLocale, type Locale } from '@sohanscript/i18n/runtime';

export interface LocaleMeta {
	locale: Locale;
	dir: 'ltr' | 'rtl';
	ogLocale: string;
	languageName: string;
	label: string;
}

const LOCALE_META: Record<Locale, Omit<LocaleMeta, 'locale'>> = {
	en: {
		dir: 'ltr',
		ogLocale: 'en_US',
		languageName: 'English',
		label: 'English',
	},
	ar: {
		dir: 'rtl',
		ogLocale: 'ar_SA',
		languageName: 'Arabic',
		label: 'العربية',
	},
	bn: {
		dir: 'ltr',
		ogLocale: 'bn_BD',
		languageName: 'Bangla',
		label: 'বাংলা',
	},
	de: {
		dir: 'ltr',
		ogLocale: 'de_DE',
		languageName: 'German',
		label: 'Deutsch',
	},
	es: {
		dir: 'ltr',
		ogLocale: 'es_ES',
		languageName: 'Spanish',
		label: 'Español',
	},
	fr: {
		dir: 'ltr',
		ogLocale: 'fr_FR',
		languageName: 'French',
		label: 'Français',
	},
	pt: {
		dir: 'ltr',
		ogLocale: 'pt_BR',
		languageName: 'Portuguese',
		label: 'Português (Brasil)',
	},
	zh: {
		dir: 'ltr',
		ogLocale: 'zh_CN',
		languageName: 'Chinese',
		label: '中文',
	},
	tr: {
		dir: 'ltr',
		ogLocale: 'tr_TR',
		languageName: 'Turkish',
		label: 'Türkçe',
	},
	ru: {
		dir: 'ltr',
		ogLocale: 'ru_RU',
		languageName: 'Russian',
		label: 'Русский',
	},
	pl: {
		dir: 'ltr',
		ogLocale: 'pl_PL',
		languageName: 'Polish',
		label: 'Polski',
	},
};

// NOTE: single read point for locale + direction facts. Call sites read here
// instead of re-deriving from getLocale()/getTextDirection() comparisons.
export const localeMeta: (locale?: Locale) => LocaleMeta = (locale) => {
	const resolved = locale ?? getLocale();
	return { locale: resolved, ...LOCALE_META[resolved] };
};

/** Writing direction for the active (or given) locale. */
export function direction(locale?: Locale): 'ltr' | 'rtl' {
	return localeMeta(locale).dir;
}

/** Boolean form of direction(); use for layout and side decisions. */
export function isRtl(locale?: Locale): boolean {
	return direction(locale) === 'rtl';
}

/** Locale-aware nav label; brand names (Fiverr/Upwork) pass through. */
export function localizeNavLabel(label: string): string {
	switch (label) {
		case 'Works':
			return m.nav_works();
		case 'Notes':
			return m.nav_notes();
		case 'Services':
			return m.nav_services();
		case 'Resume':
			return m.nav_resume();
		case 'Contact':
			return m.nav_contact();
		default:
			return label;
	}
}
